import { useEffect, useRef } from 'react'
import { ArrowRight, Plus, Trash2 } from 'lucide-react'

import { Button } from '../../ui/Button'
import { Input } from '../../ui/Field'
import { IconButton } from '../../ui/IconButton'
import { GUIDED_MAX_BODY_MIB, nextFreePort, servesHttpIngress, type GuidedPort } from '../../core/guided'
import { NO_MY_IP, PortAccess, RestrictedBadge, type MyIpState } from './PortAccess'

/** A fixed list, not derived from the image (decision D4). */
const COMMON_PORTS: Array<{ port: number; name?: string }> = [{ port: 80, name: 'HTTP' }, { port: 443, name: 'HTTPS' }, { port: 8080 }, { port: 3000 }]

export function portCountLabel(count: number, restricted = 0): string {
  return `${count} exposed port${count === 1 ? '' : 's'}${restricted > 0 ? ` · ${restricted} restricted` : ''}`
}

function removeLabel(port: GuidedPort): string {
  if (!port.container) return 'Remove empty port'
  return `Remove port ${port.container}${port.protocol === 'udp' ? ' (UDP)' : ''}`
}

/** The Networking card body: one row per exposed port under shared column headers. */
export function PortList({ ports, errors, submitted, myIp = NO_MY_IP, onChange }: { ports: GuidedPort[]; errors: Record<string, string>; submitted: boolean; myIp?: MyIpState; onChange: (ports: GuidedPort[]) => void }) {
  // The id to focus once the next render has put it in the document.
  const pendingFocus = useRef<string | null>(null)
  useEffect(() => {
    if (!pendingFocus.current) return
    document.getElementById(pendingFocus.current)?.focus()
    pendingFocus.current = null
  })

  const setPort = (index: number, change: Partial<GuidedPort>) => onChange(ports.map((item, itemIndex) => itemIndex === index ? { ...item, ...change } : item))
  // Anyone removes the key entirely, so the port is stored exactly as before this feature.
  const setAccess = (index: number, allowFrom: string[] | undefined) => onChange(ports.map((item, itemIndex) => {
    if (itemIndex !== index) return item
    const { allowFrom: _previous, ...rest } = item
    return allowFrom === undefined ? rest : { ...rest, allowFrom }
  }))
  const remove = (index: number) => {
    const next = ports.filter((_, itemIndex) => itemIndex !== index)
    pendingFocus.current = next.length > 0 ? `port-container-${Math.min(index, next.length - 1)}` : 'port-add'
    onChange(next)
  }
  const add = () => {
    pendingFocus.current = `port-container-${ports.length}`
    onChange([...ports, { container: nextFreePort(ports), protocol: 'tcp' }])
  }
  // Only TCP port 80 goes through the host's HTTP ingress, so only it has a body-size limit to set.
  const ingressIndex = ports.findIndex(servesHttpIngress)
  const ingress = ingressIndex === -1 ? undefined : ports[ingressIndex]
  const shortcuts = COMMON_PORTS.filter(({ port }) => !ports.some((item) => item.container === port && item.protocol === 'tcp'))

  return <>
    <p className="field-help">Each port is reachable from the internet unless you limit it. The host assigns the public port when the instance starts.</p>
    <div className="row-list row-list--ports" role="group" aria-label="Exposed ports">
      <div className="row-list-head" aria-hidden="true"><span>Container port</span><span>Protocol</span><span>Public port</span><span /></div>
      {/* Keyed by position, not by value: a key that changed on every keystroke remounted the row and dropped focus after each digit. */}
      {ports.map((port, index) => <div className="row-list-row" key={index}>
        {/* A text field, not type="number": no spinner, and a scroll over it cannot silently change the port. */}
        <Input id={`port-container-${index}`} label="Container port" hideLabel type="text" inputMode="numeric" pattern="[0-9]*" maxLength={5} autoComplete="off" value={port.container || ''} error={submitted ? errors[`port-${index}`] : undefined} onChange={(event) => { const digits = event.target.value.replace(/\D/g, ''); setPort(index, { container: digits === '' ? 0 : Number(digits) }) }} />
        <fieldset className="segmented row-list-protocol" id={`port-protocol-${index}`}>
          <legend className="sr-only">Protocol</legend>
          {(['tcp', 'udp'] as const).map((protocol) => <span key={protocol}>
            <input className="sr-only" type="radio" id={`port-protocol-${index}-${protocol}`} name={`port-protocol-${index}`} value={protocol} checked={port.protocol === protocol} onChange={() => setPort(index, { protocol })} />
            <label htmlFor={`port-protocol-${index}-${protocol}`}>{protocol.toUpperCase()}</label>
          </span>)}
        </fieldset>
        <span className="row-list-public"><ArrowRight aria-hidden="true" size={14} />Assigned at launch{port.allowFrom !== undefined && <RestrictedBadge count={port.allowFrom.length} />}</span>
        {/* The last port stays, but its control stays too so the column does not jump. */}
        <IconButton className="row-list-remove" label={removeLabel(port)} disabled={ports.length <= 1} onClick={() => remove(index)}><Trash2 aria-hidden="true" size={16} /></IconButton>
        <PortAccess port={port} index={index} error={errors[`port-access-${index}`]} submitted={submitted} myIp={myIp} onChange={(allowFrom) => setAccess(index, allowFrom)} />
      </div>)}
    </div>
    {ingress && <div className="port-http-body">
      <Input id="port-http-body" label="Largest request on port 80 (MiB)" type="text" inputMode="numeric" pattern="[0-9]*" maxLength={3} autoComplete="off" placeholder="1" description={`Larger uploads are refused. Empty means 1 MiB; the most is ${GUIDED_MAX_BODY_MIB}.`} value={ingress.maxBodyMiB ?? ''} error={submitted ? errors.httpbody : undefined} onChange={(event) => { const digits = event.target.value.replace(/\D/g, ''); setPort(ingressIndex, { maxBodyMiB: digits === '' ? undefined : Number(digits) }) }} />
    </div>}
    <div className="row-list-actions">
      <Button id="port-add" type="button" variant="secondary" size="sm" onClick={add}><Plus aria-hidden="true" size={16} />Add port</Button>
      {shortcuts.length > 0 && <>
        <span className="row-list-common">Common:</span>
        {/* A used shortcut unmounts, so focus stays in this row on "Add port" rather than falling to <body>. */}
        {shortcuts.map(({ port, name }) => <Button key={port} type="button" variant="secondary" size="sm" aria-label={`Add port ${port}${name ? ` (${name})` : ''}`} onClick={() => { pendingFocus.current = 'port-add'; onChange([...ports, { container: port, protocol: 'tcp' }]) }}>{port}{name ? ` ${name}` : ''}</Button>)}
      </>}
    </div>
  </>
}
